import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Bot, MapPin, Copy, X, Check } from 'lucide-react';
import {
  getDocumentText,
  getSelectedText,
  isWordApiAvailable,
  applyRedlineWithTrackChanges,
  insertClauseRelativeToAnchor,
  removeDuplicateClause,
  goToTextInDocument,
} from '../utils/wordApi';
import { resolveContractContext } from '../utils/contractResolver';
import { renderFormattedText } from '../utils/markdown';
import { TRACKED_CHANGES_GUIDANCE } from '../utils/aiGuidance';

/** Copy text to clipboard — falls back to execCommand inside the add-in iframe
 *  where the async Clipboard API is often blocked by permissions policy. */
async function copyToClipboard(text: string): Promise<boolean> {
  const fallback = (): boolean => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return fallback();
    }
  }
  return fallback();
}

interface DocEdit {
  anchor: string;
  replacement: string;
  reason?: string;
  // How the edit is applied:
  //  - 'surgical' (default): minimal in-place replace of the anchor (only the
  //    words that changed are struck).
  //  - 'paragraph': rewrite the whole enclosing paragraph (minimal-diff).
  //  - 'insert': add `replacement` as new paragraph(s) relative to the anchor,
  //    leaving the anchor untouched (new clause/section). `position` = after/before.
  //  - 'delete_duplicate': `anchor` is a clause that appears MORE THAN ONCE; keep
  //    the first copy and delete the rest (replacement is ignored/empty).
  redline_mode?: 'surgical' | 'paragraph' | 'insert' | 'delete_duplicate';
  position?: 'after' | 'before';
}

/** A clarify option the user can click to disambiguate a request. */
interface ClarifyOption {
  label: string;
  value: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

/** An edit surfaced in an assistant message — for a Jump or Copy button. */
interface EditRef {
  label: string;
  /** Text to locate in the document (the applied replacement) for Jump. */
  jumpText?: string;
  /** Which occurrence of jumpText to scroll to (for multi-place edits). */
  occurrenceIndex?: number;
  /** Suggested new text to copy when the change couldn't be applied. */
  copyText?: string;
}

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: string;
  /** Clarify buttons — present only on a 'clarify'-kind assistant message. */
  options?: ClarifyOption[];
  /** Applied changes → "Jump to change" buttons. */
  applied?: EditRef[];
  /** Changes that couldn't be placed → "Copy new text" buttons. */
  unlocatable?: EditRef[];
  /** Verbatim doc snippets an ANSWER refers to → "Jump" buttons to read them. */
  citations?: EditRef[];
  /** The user's selected text shown as a chip on their message. */
  selectedText?: string;
}

interface CurrentContract {
  contractId: string;
  tenantId: string;
  templateId: string;
}

// Chat history is persisted in sessionStorage so it survives navigating
// between the add-in's tabs (Home / Review / Risk / More) but is wiped when
// the user closes the task pane, closes the Word tab, or leaves the editor.
const CHAT_STORAGE_KEY = 'ezylex-chat-messages';
// The contract the persisted history belongs to. The task pane can be reused
// across documents in the same Word session (sessionStorage survives), so we
// tag the history with its owning contract and wipe it when the document
// changes — otherwise one contract's chat bleeds into the next.
const CHAT_OWNER_KEY = 'ezylex-chat-contract';
// Liveness heartbeat used to tell a quick pane reload (keep history) apart from
// a full page reload / navigation / fresh open (wipe). While the pane is open
// it stamps `now` here every few seconds and on unload; on mount, if the gap
// since the last stamp is small the pane merely reloaded, so history survives.
// A full page reload reloads Word (a long gap) and closing the tab clears
// sessionStorage entirely — both start clean.
const CHAT_ALIVE_KEY = 'ezylex-chat-alive';
const CHAT_ALIVE_WINDOW_MS = 8000;

function isRecentlyAlive(): boolean {
  try {
    const raw = sessionStorage.getItem(CHAT_ALIVE_KEY);
    const alive = raw ? parseInt(raw, 10) : 0;
    return !!alive && Date.now() - alive <= CHAT_ALIVE_WINDOW_MS;
  } catch {
    return false;
  }
}

function loadPersistedMessages(): Message[] {
  try {
    // Only restore history if the pane was alive moments ago (a quick reload).
    // Otherwise this is a full page reload / navigation / fresh open → start
    // clean and drop any stale history.
    if (!isRecentlyAlive()) {
      sessionStorage.removeItem(CHAT_STORAGE_KEY);
      sessionStorage.removeItem(CHAT_OWNER_KEY);
      return [];
    }
    const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m) =>
        m &&
        typeof m.id === 'string' &&
        typeof m.text === 'string' &&
        typeof m.isUser === 'boolean' &&
        typeof m.timestamp === 'string',
    );
  } catch {
    return [];
  }
}

export function Chatbot() {
  const [messages, setMessages] = useState<Message[]>(loadPersistedMessages);
  const [inputText, setInputText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [documentContext, setDocumentContext] = useState<string>('');
  const [isLoadingContext, setIsLoadingContext] = useState<boolean>(true);
  const [currentContract, setCurrentContract] = useState<CurrentContract | null>(null);
  const [selection, setSelection] = useState<string>('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Track the user's Word selection so we can show a chip and scope edits to it.
  useEffect(() => {
    if (!isWordApiAvailable()) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const t = (await getSelectedText()).trim();
        if (!cancelled) setSelection(t.length > 0 && t.length <= 3000 ? t : '');
      } catch {
        if (!cancelled) setSelection('');
      }
    };
    refresh();
    const Off = (window as any).Office;
    let handlerAdded = false;
    try {
      Off?.context?.document?.addHandlerAsync?.(
        Off.EventType.DocumentSelectionChanged,
        refresh,
        () => {
          handlerAdded = true;
        },
      );
    } catch {
      /* selection events unsupported — chip just won't live-update */
    }
    return () => {
      cancelled = true;
      if (handlerAdded) {
        try {
          Off?.context?.document?.removeHandlerAsync?.(
            Off.EventType.DocumentSelectionChanged,
            { handler: refresh },
          );
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  // Mirror every messages update to sessionStorage so navigating away from
  // this tab and back restores the conversation.
  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
    } catch {
      // sessionStorage may be unavailable (private mode / quota) — ignore.
    }
  }, [messages]);

  // Liveness heartbeat: stamp "now" while the pane is open (interval + on
  // unload). On the next mount, loadPersistedMessages keeps history only if the
  // gap since the last stamp is small — i.e. the pane merely reloaded. A full
  // page reload reloads Word (a long gap → wipe); navigating away or closing the
  // tab leaves a stale/absent stamp (→ wipe). Add-in tab navigation doesn't
  // unload the iframe, so history is untouched.
  useEffect(() => {
    const beat = () => {
      try {
        sessionStorage.setItem(CHAT_ALIVE_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    };
    beat();
    const id = window.setInterval(beat, 3000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', beat);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', beat);
    };
  }, []);

  useEffect(() => {
    // Check if there's selected text from AI Assistant button
    const selectedText = sessionStorage.getItem('ezylex-ai-selected-text') || localStorage.getItem('ezylex-ai-selected-text');
    if (selectedText) {
      setInputText(`Analyze this text: "${selectedText}"`);
      sessionStorage.removeItem('ezylex-ai-selected-text');
      localStorage.removeItem('ezylex-ai-selected-text');
    }

    // Load contract context and document text (works with both WOPI and SharePoint/Auth modes)
    const loadContext = async () => {
      try {
        setIsLoadingContext(true);

        // Resolve contract context
        const ctx = await resolveContractContext();
        if (ctx) setCurrentContract(ctx);
        // Scope chat history to this contract: if the stored history belongs to
        // a different document, wipe it so we don't show the last doc's chat.
        if (ctx?.contractId) {
          try {
            const owner = sessionStorage.getItem(CHAT_OWNER_KEY);
            if (owner && owner !== ctx.contractId) {
              sessionStorage.removeItem(CHAT_STORAGE_KEY);
              setMessages([]);
            }
            sessionStorage.setItem(CHAT_OWNER_KEY, ctx.contractId);
          } catch {
            /* storage unavailable — history just won't be scoped */
          }
        }

        // Load document text
        if (isWordApiAvailable()) {
          const text = await getDocumentText();
          setDocumentContext(text.substring(0, 8000));
        }
      } catch (error) {
        console.error('Error loading context:', error);
      } finally {
        setIsLoadingContext(false);
      }
    };
    loadContext();
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // `overrideText` is supplied when the user clicks a clarify option button —
  // it sends that option's label as the next user turn.
  const sendMessage = async (overrideText?: string): Promise<void> => {
    const text = (overrideText ?? inputText).trim();
    if (!text || isLoading) return;

    // Clarify-option clicks don't carry a selection scope.
    const scopedSelection = overrideText === undefined ? selection : '';

    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      isUser: true,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      selectedText: scopedSelection || undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    if (overrideText === undefined) {
      setInputText('');
      setSelection(''); // one selection per message, like the reference
    }
    setIsLoading(true);

    try {
      // Pull the freshest document text so AI sees redlines from previous
      // turns (otherwise it works off the initial mount snapshot).
      let freshDocText = documentContext;
      let selectedText = '';
      if (isWordApiAvailable()) {
        try {
          const latest = await getDocumentText();
          freshDocText = latest.substring(0, 12000);
          if (latest && latest.length > 0) setDocumentContext(freshDocText);
        } catch {
          // keep cached context
        }
        // The user's highlighted selection scopes edits/questions to it.
        try {
          const sel = (await getSelectedText()).trim();
          if (sel.length > 0 && sel.length <= 3000) selectedText = sel;
        } catch {
          // no selection / unreadable — ignore
        }
      }

      // System prompt — AI may (a) answer in prose, or (b) edit the document
      // by returning structured JSON. The frontend applies any edits with
      // Word track-changes ON so the user can review them as redlines.
      const editingInstructions = `
OUTPUT FORMAT — return a SINGLE JSON object (no markdown fences, no text outside JSON):
{
  "kind": "answer" | "clarify" | "apply",
  "reply": "<message to the user, markdown allowed>",
  "options": [ { "label": "<short locator, e.g. a section reference>", "value": "<short id>" } ],
  "citations": [ "<verbatim snippet from the document your answer refers to>" ],
  "edits": [
    { "redline_mode": "surgical" | "paragraph" | "insert" | "delete_duplicate",
      "anchor": "<verbatim text from the document>",
      "replacement": "<new text>",
      "position": "after" | "before",
      "reason": "<1 short sentence>" }
  ]
}

RESPONSE MODES — pick exactly one "kind". DEFAULT TO "apply".
1. "answer" — the user asked a QUESTION and wants no change. Put the answer in "reply"; omit "edits"/"options".
2. "clarify" — use SPARINGLY. Only when the user's INTENT is genuinely vague and you truly cannot decide what to do (e.g. "tighten this", "improve this section" with several distinct meanings). Ask ONE short question and give 2–4 "options"; make NO edits. Do NOT use clarify merely because text repeats or a location is unclear — in that case just apply your best interpretation (the tool reports anything it can't place). The user's NEXT message is the option they picked.
3. "apply" — a change was requested and you can act. Put a SHORT PAST-TENSE narration in "reply" ("Done — shortened the term to 3 years.") and the changes in "edits".

DECISION — strongly bias to "apply". A clear instruction — "remove the duplicate clause", "change X to Y", "add a clause", "make it mutual", "delete this sentence" — is ALWAYS "apply", NEVER "clarify", even when the target text repeats. Do NOT ask the user which occurrence, and do NOT ask them to confirm — just make your best edit. Only fall back to "clarify" for genuinely vague intent you cannot act on at all.

DUPLICATES — when the user asks to remove/delete a DUPLICATED clause, section, heading, or sentence (something that appears more than once), emit ONE edit with "redline_mode":"delete_duplicate" and "anchor" = the verbatim body text of the duplicated clause (the repeated paragraph, exactly as written, ≤255 chars, single line). The tool keeps the first copy and deletes the rest (including a repeated heading above it). Do NOT try to target "the second one" yourself and do NOT use surgical/paragraph mode for de-duplication.

EDIT MODES (inside "edits"):
- "surgical" (default): a SMALL in-place change. "anchor" = the EXACT verbatim text being replaced (the shortest substring containing only the words that change). "replacement" = the new text for that anchor.
- "paragraph": the ENTIRE enclosing paragraph is being rewritten (>~50% differs). "anchor" = a SHORT distinctive verbatim fragment of that paragraph; "replacement" = the FULL new paragraph text.
- "insert": ADD new text (a new clause/section/sentence). "anchor" = a nearby existing heading or the last sentence of the neighbouring clause; "replacement" = ONLY the new text (do NOT restate the anchor); "position" = "after" (default) or "before".
- "delete_duplicate": REMOVE a repeated clause/section/sentence. "anchor" = the verbatim repeated text (single line, ≤255 chars); "replacement" = "" (empty). Keeps the first copy, deletes the rest.

ANCHOR RULES (surgical & paragraph):
- "anchor" MUST be a VERBATIM substring of the document — copy it exactly (words, casing, punctuation). Never paraphrase.
- SINGLE LINE — no line breaks — and ≤ 255 characters.
- The anchor is replaced in EVERY place it appears. If the same change should apply to all occurrences, pick the shared phrase and emit ONE edit. If only one specific occurrence should change, extend the anchor with enough surrounding words so it matches ONLY that spot. Use "paragraph" mode when the whole paragraph is rewritten.
- MINIMAL DIFF: change the fewest words. "replacement" differs from "anchor" ONLY by the words that must change — never rewrite the surrounding sentence.

CITATIONS: whenever your answer refers to specific text in the document (a clause, a defined term, a value, a location), add the exact VERBATIM snippet(s) you are referring to in "citations" — copied character-for-character from the document, each a short distinctive phrase (≤ 200 chars, single line). These become "Jump" buttons so the user can scroll to and read that text. Use citations on "answer" and "clarify" turns; for "apply" turns the edits already carry their own jump links.

SELECTION: when a SELECTED_TEXT block is present, treat it as the SCOPE — anchors must lie within it and answers focus on it — UNLESS the user says "everywhere" / "in all clauses".

TONE: concise and professional. NEVER expose internal mechanics in "reply" — the words "anchor", "surgical", "paragraph mode", "redline", "insert mode" must not appear. For "apply", write in PAST tense ("Done — …"), never "I will…". Do not claim a number of changes you can't verify — the tool applies your edits and shows them as tracked changes.`;

      const selectionBlock = selectedText
        ? `\nSELECTED_TEXT (the user highlighted this — treat as the scope unless they broaden it):\n"""\n${selectedText}\n"""\n`
        : '';

      const systemPrompt = freshDocText
        ? `You are Ezylex AI, an in-document legal assistant for Ezylex CLM. You can answer questions about the contract AND make edits to it on the user's behalf.

Here is the contract document text:
${freshDocText}
${selectionBlock}
${editingInstructions}
${TRACKED_CHANGES_GUIDANCE}`
        : `You are Ezylex AI, a legal assistant for Ezylex CLM. The document text is not currently loaded, so you can only answer questions — do not include any "edits" in your response.
${editingInstructions}
${TRACKED_CHANGES_GUIDANCE}`;

      if (!currentContract?.tenantId) {
        throw new Error('Contract context not available. Please reload the add-in.');
      }

      // Build conversation history for context
      const conversationHistory = messages.map((msg) => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text,
      }));
      conversationHistory.push({ role: 'user', content: userMessage.text });

      // Create a combined prompt with conversation history
      const conversationPrompt = conversationHistory.map(msg => 
        `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
      ).join('\n\n');

      const response = await fetch(`${API_BASE_URL}/api/addin/ai-analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-addin-token': API_TOKEN,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          prompt: conversationPrompt,
          systemMessage: systemPrompt,
          temperature: 0.4,
          maxTokens: 2500,
          tenantId: currentContract.tenantId,
          jsonMode: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get response');
      }

      const data = await response.json();
      const rawContent = (data.content || '').toString();

      // Parse the AI's JSON envelope { kind, reply, options?, edits? }. If
      // parsing fails, fall back to displaying the raw text.
      let parsedReply: string = rawContent;
      let kind: 'answer' | 'clarify' | 'apply' = 'answer';
      let options: ClarifyOption[] = [];
      let edits: DocEdit[] = [];
      let citations: string[] = [];
      try {
        const cleaned = rawContent
          .replace(/^```json\s*/i, '')
          .replace(/^```\s*/i, '')
          .replace(/```\s*$/i, '')
          .trim();
        const objMatch = cleaned.match(/\{[\s\S]*\}/);
        const json = JSON.parse(objMatch ? objMatch[0] : cleaned);
        if (typeof json?.reply === 'string') parsedReply = json.reply;
        if (json?.kind === 'clarify' || json?.kind === 'apply' || json?.kind === 'answer') {
          kind = json.kind;
        }
        if (Array.isArray(json?.options)) {
          options = json.options
            .filter((o: any) => o && typeof o.label === 'string' && o.label.trim().length > 0)
            .map((o: any) => ({
              label: o.label.trim(),
              value: typeof o.value === 'string' && o.value.trim() ? o.value.trim() : o.label.trim(),
            }));
        }
        if (Array.isArray(json?.edits)) {
          edits = json.edits
            .filter(
              (e: any) =>
                e &&
                typeof e.anchor === 'string' &&
                e.anchor.trim().length > 0 &&
                // delete_duplicate needs no replacement text; others do.
                (typeof e.replacement === 'string' || e.redline_mode === 'delete_duplicate'),
            )
            .map((e: any) => {
              // Accept new redline_mode; fall back to legacy op (after/before/replace).
              let mode: DocEdit['redline_mode'] = e.redline_mode;
              let position: DocEdit['position'] = e.position === 'before' ? 'before' : e.position === 'after' ? 'after' : undefined;
              if (mode !== 'surgical' && mode !== 'paragraph' && mode !== 'insert' && mode !== 'delete_duplicate') {
                if (e.op === 'after' || e.op === 'before') {
                  mode = 'insert';
                  position = e.op;
                } else {
                  mode = 'surgical';
                }
              }
              if (mode === 'insert' && position !== 'before') position = 'after';
              return {
                anchor: e.anchor,
                replacement: typeof e.replacement === 'string' ? e.replacement : '',
                reason: typeof e.reason === 'string' ? e.reason : undefined,
                redline_mode: mode,
                position,
              };
            });
        }
        if (Array.isArray(json?.citations)) {
          citations = json.citations
            .filter((c: any) => typeof c === 'string' && c.trim().length > 0)
            .map((c: string) => c.replace(/\s+/g, ' ').trim().slice(0, 255))
            .slice(0, 6);
        }
        // Infer kind when the model omitted it.
        if (!json?.kind) kind = edits.length > 0 ? 'apply' : options.length > 0 ? 'clarify' : 'answer';
      } catch {
        // Not JSON — show raw text as-is.
      }

      // Apply edits (only on an 'apply' turn) with Track Changes ON. Collect
      // applied changes (→ "Jump to change") and unlocatable ones (→ "Copy new
      // text" so the user can paste it manually).
      const appliedRefs: EditRef[] = [];
      const unlocatableRefs: EditRef[] = [];
      const failNotes: string[] = [];
      let appliedEditCount = 0; // EDITS that landed (one edit may hit many places)
      if (kind === 'apply' && edits.length > 0 && isWordApiAvailable()) {
        for (const edit of edits) {
          let result;
          if (edit.redline_mode === 'delete_duplicate') {
            result = await removeDuplicateClause(edit.anchor);
          } else if (edit.redline_mode === 'insert') {
            result = await insertClauseRelativeToAnchor(edit.anchor, edit.replacement, edit.position ?? 'after');
          } else {
            result = await applyRedlineWithTrackChanges(
              edit.anchor,
              edit.replacement,
              edit.redline_mode === 'paragraph' ? 'paragraph' : 'surgical',
            );
          }
          const label =
            (edit.reason && edit.reason.trim()) ||
            (edit.replacement || edit.anchor).replace(/\s+/g, ' ').trim().slice(0, 60);
          if (result.success) {
            appliedEditCount++;
            // For a deletion the "new text" is gone, so Jump lands on the kept
            // copy (the anchor). Otherwise jump to the text we wrote.
            const jump = (edit.replacement || edit.anchor).slice(0, 255);
            const locs = result.locations && result.locations.length ? result.locations : [label];
            // One entry per place the edit landed, each Jump targeting its own
            // occurrence — so a multi-place change lists every location.
            locs.forEach((loc, idx) => {
              appliedRefs.push({
                label: (loc && loc.trim()) || label,
                jumpText: jump,
                occurrenceIndex: idx,
              });
            });
          } else if (edit.replacement && edit.replacement.trim()) {
            // Only offer a Copy button when there's suggested text to copy.
            unlocatableRefs.push({ label, copyText: edit.replacement });
          } else if (result.error) {
            failNotes.push(result.error);
          }
        }
      }

      const requested = kind === 'apply' ? edits.length : 0;

      // Compose the reply. When NO edit landed, override the model's "Done…"
      // narration (it can't have succeeded) with an honest message; when some
      // landed, keep the narration and note any misses. The per-location list
      // (with its "N changes applied" header) renders separately below.
      let finalText = parsedReply || 'Sorry, I could not generate a response.';
      if (kind === 'apply' && requested > 0) {
        if (appliedEditCount === 0) {
          // Prefer a specific engine note (e.g. "only one copy exists") when it
          // isn't just an unlocatable-text case; otherwise the honest fallback.
          if (unlocatableRefs.length === 0 && failNotes.length > 0) {
            finalText = failNotes[0];
          } else {
            finalText =
              "I couldn't make that change — I wasn't able to pin down the exact text (it may appear in " +
              'more than one place, or the wording differs slightly). Select the clause in the document and ' +
              'ask again, tell me which section you mean, or copy the suggested text below and paste it yourself.';
          }
        } else {
          const missed = requested - appliedEditCount;
          if (missed > 0) {
            finalText =
              `${finalText}\n\n---\n${missed} of ${requested} couldn't be located and ` +
              `${missed === 1 ? 'was' : 'were'} not applied — copy the suggested text below to add ` +
              `${missed === 1 ? 'it' : 'them'} manually.`;
          }
        }
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: finalText,
        isUser: false,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        options: kind === 'clarify' && options.length > 0 ? options : undefined,
        applied: appliedRefs.length > 0 ? appliedRefs : undefined,
        unlocatable: unlocatableRefs.length > 0 ? unlocatableRefs : undefined,
        // Citations only make sense when we didn't already apply edits.
        citations:
          appliedRefs.length === 0 && citations.length > 0
            ? citations.map((c) => ({ label: c, jumpText: c }))
            : undefined,
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: error instanceof Error ? error.message : 'Failed to send message. Please try again.',
        isUser: false,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendDisabled = isLoading || !inputText.trim() || isLoadingContext;

  return (
    <div
      className="flex-1 min-h-0 flex flex-col"
      style={{ fontSize: '13px', background: 'var(--bg-soft)' }}
    >
      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8 px-4">
            <div
              className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-3"
              style={{ background: 'var(--brand-tint)' }}
            >
              <Bot className="w-7 h-7" style={{ color: 'var(--brand-3)' }} />
            </div>
            <p className="text-[13.5px] font-semibold text-[var(--ink)] mb-1.5">
              {isLoadingContext
                ? 'Loading document context…'
                : documentContext
                ? 'Ask me anything about your contract'
                : 'How can I help you today?'}
            </p>
            {documentContext && (
              <p className="text-[11.5px] text-[var(--ink-3)] leading-relaxed">
                I have access to your document and can answer questions, draft clauses, and provide analysis.
              </p>
            )}
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={'max-w-[85%] rounded-[var(--r-lg)] px-3.5 py-2.5 ' +
                (message.isUser ? 'text-white' : 'bg-white border')}
              style={
                message.isUser
                  ? { background: 'var(--brand)' }
                  : { borderColor: 'var(--line)' }
              }
            >
              {message.isUser ? (
                <>
                  {message.selectedText && (
                    <div
                      className="mb-1.5 rounded-[var(--r-md)] px-2 py-1"
                      style={{
                        background: 'rgba(255,255,255,.16)',
                        borderLeft: '2px solid rgba(255,255,255,.55)',
                      }}
                    >
                      <p
                        className="uppercase tracking-wide mb-0.5"
                        style={{ fontSize: '9.5px', color: 'rgba(255,255,255,.75)' }}
                      >
                        Selected text
                      </p>
                      <p
                        style={{
                          fontSize: '11.5px',
                          lineHeight: 1.4,
                          color: 'rgba(255,255,255,.92)',
                          wordBreak: 'break-word',
                        }}
                      >
                        {message.selectedText.length > 220
                          ? message.selectedText.slice(0, 220) + '…'
                          : message.selectedText}
                      </p>
                    </div>
                  )}
                  <p style={{ fontSize: '13px', lineHeight: 1.55, wordBreak: 'break-word' }}>
                    {message.text}
                  </p>
                </>
              ) : (
                <div style={{ fontSize: '13px', lineHeight: 1.55, wordBreak: 'break-word' }}>
                  {renderFormattedText(message.text)}
                </div>
              )}
              {!message.isUser && message.options && message.options.length > 0 && (
                <div className="flex flex-col gap-1.5 mt-2">
                  {message.options.map((opt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => sendMessage(opt.label)}
                      disabled={isLoading}
                      className="text-left rounded-[var(--r-md)] px-3 py-1.5 border transition-colors disabled:opacity-50 hover:bg-[var(--bg-soft)]"
                      style={{ fontSize: '12.5px', borderColor: 'var(--line)', color: 'var(--brand)' }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {!message.isUser && message.applied && message.applied.length > 0 && (
                <div
                  className="mt-2 rounded-[var(--r-md)] border overflow-hidden"
                  style={{ borderColor: 'var(--line)' }}
                >
                  <div
                    className="px-2.5 py-1.5 uppercase tracking-wide"
                    style={{ fontSize: '9.5px', color: 'var(--ink-3)', background: 'var(--bg-soft)' }}
                  >
                    {message.applied.length === 1
                      ? '1 change applied'
                      : `${message.applied.length} changes applied`}
                  </div>
                  {message.applied.map((ref, i) => (
                    <div
                      key={`ap-${i}`}
                      className="flex items-center gap-2 px-2.5 py-1.5 border-t"
                      style={{ borderColor: 'var(--line)' }}
                    >
                      <span
                        className="flex-shrink-0 grid place-items-center rounded-full font-semibold"
                        style={{
                          fontSize: '10px',
                          width: 18,
                          height: 18,
                          background: 'var(--brand-tint)',
                          color: 'var(--brand-3)',
                        }}
                      >
                        {i + 1}
                      </span>
                      <span
                        className="flex-1 min-w-0 truncate"
                        style={{ fontSize: '11.5px', color: 'var(--ink-2)' }}
                        title={ref.label}
                      >
                        {ref.label}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          ref.jumpText && goToTextInDocument(ref.jumpText, ref.occurrenceIndex ?? 0)
                        }
                        className="flex-shrink-0 inline-flex items-center gap-1 rounded-[var(--r-md)] px-2 py-0.5 border transition-colors hover:bg-[var(--bg-soft)]"
                        style={{ fontSize: '11px', borderColor: 'var(--line)', color: 'var(--brand)' }}
                      >
                        <MapPin className="w-3 h-3" />
                        Jump
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {!message.isUser && message.citations && message.citations.length > 0 && (
                <div className="flex flex-col gap-1.5 mt-2">
                  <p
                    className="uppercase tracking-wide"
                    style={{ fontSize: '9.5px', color: 'var(--ink-4)' }}
                  >
                    Referenced in the document
                  </p>
                  {message.citations.map((ref, i) => (
                    <button
                      key={`ci-${i}`}
                      type="button"
                      onClick={() => ref.jumpText && goToTextInDocument(ref.jumpText)}
                      className="inline-flex items-center gap-1.5 self-start max-w-full rounded-[var(--r-md)] px-2.5 py-1 border transition-colors hover:bg-[var(--bg-soft)]"
                      style={{ fontSize: '11.5px', borderColor: 'var(--line)', color: 'var(--brand)' }}
                      title={ref.label}
                    >
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{ref.label}</span>
                    </button>
                  ))}
                </div>
              )}
              {!message.isUser && message.unlocatable && message.unlocatable.length > 0 && (
                <div className="flex flex-col gap-2 mt-2">
                  {message.unlocatable.map((ref, i) => {
                    const key = `${message.id}-un-${i}`;
                    return (
                      <div
                        key={key}
                        className="rounded-[var(--r-md)] border p-2"
                        style={{ borderColor: 'var(--line)', background: 'var(--bg-soft)' }}
                      >
                        <p
                          className="uppercase tracking-wide mb-1"
                          style={{ fontSize: '9.5px', color: 'var(--ink-4)' }}
                        >
                          Couldn't locate — copy &amp; find/replace
                        </p>
                        <p
                          style={{ fontSize: '12px', lineHeight: 1.45, color: 'var(--ink-2)', wordBreak: 'break-word' }}
                        >
                          {ref.copyText}
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            const ok = await copyToClipboard(ref.copyText || '');
                            if (ok) {
                              setCopiedKey(key);
                              window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
                            }
                          }}
                          className="inline-flex items-center gap-1.5 self-start rounded-[var(--r-md)] px-2.5 py-1 border transition-colors hover:bg-white mt-1.5"
                          style={{ fontSize: '12px', borderColor: 'var(--line)', color: 'var(--brand)' }}
                        >
                          {copiedKey === key ? (
                            <>
                              <Check className="w-3.5 h-3.5" /> Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" /> Copy text
                            </>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p
                className="text-[10.5px] mt-1.5"
                style={{ color: message.isUser ? 'rgba(255,255,255,.7)' : 'var(--ink-4)' }}
              >
                {message.timestamp}
              </p>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div
              className="bg-white border rounded-[var(--r-lg)] px-3.5 py-2.5"
              style={{ borderColor: 'var(--line)' }}
            >
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--brand-3)' }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input — visually blends with the surrounding bg-soft so it
         doesn't look like a second footer bar above the bottom nav. */}
      <div
        className="flex-shrink-0 px-3 py-2"
        style={{ background: 'var(--bg-soft)' }}
      >
        {selection && (
          <div
            className="mb-2 flex items-start gap-2 rounded-[var(--r-md)] border px-2.5 py-1.5"
            style={{ borderColor: 'var(--line)', background: 'var(--bg)' }}
          >
            <div className="flex-1 min-w-0">
              <p
                className="uppercase tracking-wide mb-0.5"
                style={{ fontSize: '9.5px', color: 'var(--brand)' }}
              >
                Selection
              </p>
              <p
                className="truncate"
                style={{ fontSize: '11.5px', color: 'var(--ink-2)' }}
                title={selection}
              >
                {selection}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelection('')}
              className="flex-shrink-0 rounded-full p-0.5 hover:bg-[var(--bg-soft)]"
              style={{ color: 'var(--ink-4)' }}
              title="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message…"
            className="flex-1 px-3 py-2 rounded-[var(--r-lg)] resize-none focus:outline-none focus:ring-2 focus:ring-offset-0"
            rows={1}
            style={{
              fontSize: '13px',
              border: '1px solid var(--line)',
              background: 'var(--bg)',
              minHeight: 40,
              maxHeight: 80,
            }}
            disabled={isLoading || isLoadingContext}
          />
          <button
            type="button"
            onClick={() => sendMessage()}
            disabled={sendDisabled}
            className="flex-shrink-0 h-[40px] w-[40px] rounded-[var(--r-lg)] grid place-items-center text-white transition-colors disabled:cursor-not-allowed"
            style={{
              background: sendDisabled ? 'var(--ink-4)' : 'var(--brand)',
            }}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
